/**
 * Catalog — choose WHICH spreadsheet the app syncs from and, per tab, how the
 * product page and the share message look:
 *
 *  - Table columns: shown/hidden + ORDER (app_config/sheetSync — the worker
 *    bakes the order into product.attrs on its next pass, ≤1 min), plus
 *    display-label renames and visual section gaps (app_config/displayFormat —
 *    applied at render time, live immediately).
 *  - Share/copy message: its OWN field order, per-field hide and blank-line
 *    separators (app_config/displayFormat, live immediately). Fallback when
 *    never configured: attrs order with a blank line before the UPI row.
 *  - Category tab order in the dealer app (app_config/global.categoryOrder —
 *    admin-only field, live immediately; see CategoryOrderCard).
 *
 * Listing/inspection go through the sync worker (only it holds the service
 * account); both config docs are written straight to Firestore (admins may
 * write app_config per the rules). Previews use the SAME shared helpers the
 * customer app renders with, fed by a sample product of each tab.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import type {
  DisplayFormatConfig,
  Product,
  ShareLayoutItem,
  SheetSyncConfig,
  TabDisplayFormat,
} from '@mitang/shared';
import {
  buildSpecShareLines,
  COLLECTIONS,
  DISPLAY_FORMAT_DOC,
  displayLabel,
  PAYMENT_ROW_FALLBACK,
  SHEET_SYNC_DOC,
  specTableRows,
} from '@mitang/shared';
import { auth, db } from '../lib/firebase';
import { Card, SectionHeader, Button, Badge } from '../components/ui';
import { LoadingState, ErrorState } from '../components/States';
import { PhonePreview, ShareMessagePreview } from '../components/CatalogPreviews';
import { CategoryOrderCard } from '../components/CategoryOrderCard';
import { useToast } from '../lib/toast';

const SYNC_ENDPOINT: string = import.meta.env.VITE_SYNC_ENDPOINT ?? '';
/** Whose Drive listing the worker exposes — differs between prod and staging. */
const SHEETS_SA_EMAIL: string =
  import.meta.env.VITE_SHEETS_SA_EMAIL ?? 'firebase-adminsdk-fbsvc@anyway-c1d31.iam.gserviceaccount.com';

interface SheetListing { id: string; name: string; modifiedTime: string }
interface TabInspection { tab: string; columns: string[]; priceLike: string[]; rowCount: number }

/** One table column in the editor: sync config + presentation config. */
interface ColRow {
  name: string;
  keep: boolean;
  priceLike: boolean;
  /** Display-label override ('' = none). */
  label: string;
  /** Visual section gap above this row in the spec table. */
  breakBefore: boolean;
}
type TabsModel = Record<string, ColRow[]>;

/** One row of the share-message editor (id is a stable React key). */
type ShareRow =
  | { id: string; kind: 'field'; name: string; show: boolean }
  | { id: string; kind: 'blank' };
type ShareModel = Record<string, ShareRow[]>;

async function callWorker<T>(action: string, extra: Record<string, unknown> = {}): Promise<T> {
  if (!SYNC_ENDPOINT) throw new Error('VITE_SYNC_ENDPOINT is not configured.');
  const idToken = await auth.currentUser?.getIdToken();
  const res = await fetch(SYNC_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ action, idToken, ...extra }),
  });
  const data = (await res.json()) as T & { ok: boolean; message?: string };
  if (!data.ok) throw new Error(data.message ?? `${action} failed`);
  return data;
}

/** Move an array element (returns a copy; no-op when out of range). */
function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (to < 0 || to >= arr.length || from === to) return arr;
  const copy = arr.slice();
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
}

/**
 * Keep the share editor consistent with the kept table columns: drop fields
 * that are no longer kept, append newly-kept ones (visible) at the end. Blank
 * separators survive untouched.
 */
function reconcileShare(items: ShareRow[], keptNames: string[]): ShareRow[] {
  const keptSet = new Set(keptNames);
  const out = items.filter((it) => it.kind === 'blank' || keptSet.has(it.name));
  const claimed = new Set(out.flatMap((it) => (it.kind === 'field' ? [it.name] : [])));
  for (const name of keptNames) {
    if (!claimed.has(name)) out.push({ id: `f-${name}`, kind: 'field', name, show: true });
  }
  return out;
}

/**
 * Seed a tab's share editor when nothing is saved yet: kept columns in table
 * order with a blank line before the first UPI-like one — exactly the legacy
 * behavior, so saving without touching anything changes nothing for dealers.
 */
function defaultShare(keptNames: string[]): ShareRow[] {
  const out: ShareRow[] = [];
  let separated = false;
  for (const name of keptNames) {
    if (!separated && PAYMENT_ROW_FALLBACK.test(name)) {
      out.push({ id: 'b-upi', kind: 'blank' });
      separated = true;
    }
    out.push({ id: `f-${name}`, kind: 'field', name, show: true });
  }
  return out;
}

export function CatalogPage(): JSX.Element {
  const toast = useToast();
  const [sheets, setSheets] = useState<SheetListing[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [saved, setSaved] = useState<SheetSyncConfig | null>(null);
  const [savedFmt, setSavedFmt] = useState<DisplayFormatConfig | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState('');
  const [tabs, setTabs] = useState<TabsModel | null>(null);
  const [share, setShare] = useState<ShareModel>({});
  /** The sheet's own column order per tab (for "Reset to sheet order"). */
  const [sheetOrder, setSheetOrder] = useState<Record<string, string[]>>({});
  /** One sample product per tab, feeding the previews. */
  const [samples, setSamples] = useState<Record<string, Product | null>>({});
  const [inspecting, setInspecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const blankSeq = useRef(0);
  /** Drag-and-drop source: which list ('cols:TAB' / 'share:TAB') and index. */
  const dragFrom = useRef<{ list: string; index: number } | null>(null);

  // Current saved configs (live).
  useEffect(() => {
    const ref = doc(db, COLLECTIONS.appConfig, SHEET_SYNC_DOC);
    return onSnapshot(ref, (snap) => setSaved((snap.data() as SheetSyncConfig | undefined) ?? null));
  }, []);
  useEffect(() => {
    const ref = doc(db, COLLECTIONS.appConfig, DISPLAY_FORMAT_DOC);
    return onSnapshot(ref, (snap) =>
      setSavedFmt((snap.data() as DisplayFormatConfig | undefined) ?? null),
    );
  }, []);

  // Accessible spreadsheets.
  useEffect(() => {
    callWorker<{ sheets: SheetListing[] }>('list_sheets')
      .then((r) => setSheets(r.sheets))
      .catch((e: Error) => setListError(e.message));
  }, []);

  const inspect = useCallback(
    async (sheet: SheetListing) => {
      setInspecting(true);
      setSelectedId(sheet.id);
      setSelectedName(sheet.name);
      setTabs(null);
      try {
        const r = await callWorker<{ name: string; tabs: TabInspection[] }>('inspect_sheet', {
          sheetId: sheet.id,
        });
        const model: TabsModel = {};
        const shareModel: ShareModel = {};
        const orderModel: Record<string, string[]> = {};
        for (const t of r.tabs) {
          const savedCols =
            saved && saved.spreadsheetId === sheet.id ? saved.tabs?.[t.tab]?.columns : undefined;
          const fmt = savedFmt?.tabs?.[t.tab];
          const live = t.columns.filter(Boolean);
          orderModel[t.tab] = live;
          const isPrice = (n: string) => t.priceLike.includes(n);
          const breaks = new Set(fmt?.tableBreaksBefore ?? []);
          const decorate = (name: string, keep: boolean): ColRow => ({
            name,
            keep,
            priceLike: isPrice(name),
            label: fmt?.labels?.[name] ?? '',
            breakBefore: breaks.has(name),
          });
          let cols: ColRow[];
          if (savedCols && savedCols.length > 0) {
            const claimed = new Set<string>();
            cols = savedCols
              .filter((c) => live.includes(c.name))
              .map((c) => {
                claimed.add(c.name);
                return decorate(c.name, c.keep);
              });
            for (const name of live) {
              if (!claimed.has(name)) cols.push(decorate(name, !isPrice(name)));
            }
          } else {
            cols = live.map((name) => decorate(name, !isPrice(name)));
          }
          model[t.tab] = cols;

          const keptNames = cols.filter((c) => c.keep).map((c) => c.name);
          const savedShare = fmt?.share;
          if (savedShare && savedShare.length > 0) {
            const fromSaved: ShareRow[] = savedShare.map((item) =>
              item.kind === 'blank'
                ? { id: `b-${++blankSeq.current}`, kind: 'blank' }
                : { id: `f-${item.name}`, kind: 'field', name: item.name, show: item.show },
            );
            shareModel[t.tab] = reconcileShare(fromSaved, keptNames);
          } else {
            shareModel[t.tab] = defaultShare(keptNames);
          }
        }
        setTabs(model);
        setShare(shareModel);
        setSheetOrder(orderModel);

        // Sample product per tab for the previews (admin may read products).
        for (const t of r.tabs) {
          void getDocs(
            query(
              collection(db, COLLECTIONS.products),
              where('category', '==', t.tab),
              where('active', '==', true),
              limit(1),
            ),
          )
            .then((snap) => {
              const d = snap.docs[0];
              setSamples((prev) => ({
                ...prev,
                [t.tab]: d ? ({ id: d.id, ...(d.data() as Omit<Product, 'id'>) } as Product) : null,
              }));
            })
            .catch(() => setSamples((prev) => ({ ...prev, [t.tab]: null })));
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Could not read that sheet.');
        setSelectedId(null);
      } finally {
        setInspecting(false);
      }
    },
    [saved, savedFmt, toast],
  );

  /* ------------------------------ column edits ------------------------------ */

  const setCols = (tab: string, fn: (cols: ColRow[]) => ColRow[]) => {
    setTabs((prev) => (prev ? { ...prev, [tab]: fn(prev[tab]) } : prev));
  };

  const moveCol = (tab: string, from: number, to: number) => {
    setCols(tab, (cols) => moveItem(cols, from, to));
  };

  const toggleKeep = (tab: string, index: number) => {
    setTabs((prev) => {
      if (!prev) return prev;
      const cols = prev[tab].slice();
      cols[index] = { ...cols[index], keep: !cols[index].keep };
      const keptNames = cols.filter((c) => c.keep).map((c) => c.name);
      setShare((s) => ({ ...s, [tab]: reconcileShare(s[tab] ?? [], keptNames) }));
      return { ...prev, [tab]: cols };
    });
  };

  const renameCol = (tab: string, index: number, label: string) => {
    setCols(tab, (cols) => {
      const copy = cols.slice();
      copy[index] = { ...copy[index], label };
      return copy;
    });
  };

  const toggleBreak = (tab: string, index: number) => {
    setCols(tab, (cols) => {
      const copy = cols.slice();
      copy[index] = { ...copy[index], breakBefore: !copy[index].breakBefore };
      return copy;
    });
  };

  const resetToSheetOrder = (tab: string) => {
    setCols(tab, (cols) => {
      const byName = new Map(cols.map((c) => [c.name, c]));
      const ordered = (sheetOrder[tab] ?? [])
        .map((name) => byName.get(name))
        .filter((c): c is ColRow => !!c);
      // Anything not in the live sheet anymore (shouldn't happen) keeps its place.
      for (const c of cols) if (!ordered.includes(c)) ordered.push(c);
      return ordered;
    });
    toast.success(`${tab}: column order reset to the sheet's own order (not saved yet).`);
  };

  /* --------------------------- share-message edits --------------------------- */

  const setShareRows = (tab: string, fn: (rows: ShareRow[]) => ShareRow[]) => {
    setShare((prev) => ({ ...prev, [tab]: fn(prev[tab] ?? []) }));
  };

  const moveShare = (tab: string, from: number, to: number) => {
    setShareRows(tab, (rows) => moveItem(rows, from, to));
  };

  const toggleShow = (tab: string, index: number) => {
    setShareRows(tab, (rows) => {
      const copy = rows.slice();
      const row = copy[index];
      if (row.kind === 'field') copy[index] = { ...row, show: !row.show };
      return copy;
    });
  };

  const addBlank = (tab: string) => {
    setShareRows(tab, (rows) => [...rows, { id: `b-${++blankSeq.current}`, kind: 'blank' }]);
  };

  const removeBlank = (tab: string, index: number) => {
    setShareRows(tab, (rows) => rows.filter((_, i) => i !== index));
  };

  /* --------------------------------- saving --------------------------------- */

  const save = async (thenSync: boolean) => {
    if (!selectedId || !tabs) return;
    setBusy(true);
    try {
      const syncConfig: SheetSyncConfig = {
        spreadsheetId: selectedId,
        spreadsheetName: selectedName,
        tabs: Object.fromEntries(
          Object.entries(tabs).map(([tab, cols]) => [
            tab,
            { columns: cols.map(({ name, keep }) => ({ name, keep })) },
          ]),
        ),
        updatedAt: Date.now(),
      };
      // Presentation config: merge over existing tabs so tabs from another
      // spreadsheet (or ones not inspected now) are preserved.
      const fmtTabs: Record<string, TabDisplayFormat> = { ...(savedFmt?.tabs ?? {}) };
      for (const [tab, cols] of Object.entries(tabs)) {
        const labels: Record<string, string> = {};
        for (const c of cols) if (c.label.trim()) labels[c.name] = c.label.trim();
        fmtTabs[tab] = {
          labels,
          tableBreaksBefore: cols.filter((c) => c.breakBefore).map((c) => c.name),
          share: (share[tab] ?? []).map<ShareLayoutItem>((row) =>
            row.kind === 'blank' ? { kind: 'blank' } : { kind: 'field', name: row.name, show: row.show },
          ),
        };
      }
      const fmtConfig: DisplayFormatConfig = { tabs: fmtTabs, updatedAt: Date.now() };
      await setDoc(doc(db, COLLECTIONS.appConfig, SHEET_SYNC_DOC), syncConfig);
      await setDoc(doc(db, COLLECTIONS.appConfig, DISPLAY_FORMAT_DOC), fmtConfig);
      if (thenSync) {
        const r = await callWorker<{ tabs: Array<{ tab: string; status: string; activeCount: number }> }>('sync_now');
        const errTabs = r.tabs.filter((t) => t.status === 'error');
        // The worker bounds each pass (free-plan CPU budget); tabs it could not
        // finish now complete on the next minute tick.
        const laterTabs = r.tabs.filter((t) => t.status === 'deferred' || t.status === 'partial');
        const doneCount = r.tabs.length - errTabs.length - laterTabs.length;
        toast.success(
          `Synced ${doneCount} tab(s)` +
            (laterTabs.length ? `; ${laterTabs.length} finishing on the next pass (≤1 min)` : '') +
            (errTabs.length ? `; ${errTabs.length} failed (see Sync Monitor)` : ''),
        );
      } else {
        toast.success(
          'Saved. Labels, gaps and the message layout are live now; column order/visibility applies on the next sync pass (≤1 min).',
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------ drag and drop ------------------------------ */

  const dragProps = (list: string, index: number, onMove: (from: number, to: number) => void) => ({
    onDragOver: (e: React.DragEvent) => {
      if (dragFrom.current?.list === list) e.preventDefault();
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const src = dragFrom.current;
      if (src && src.list === list && src.index !== index) onMove(src.index, index);
      dragFrom.current = null;
    },
  });

  const handleProps = (list: string, index: number) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      dragFrom.current = { list, index };
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragEnd: () => {
      dragFrom.current = null;
    },
  });

  /* --------------------------------- render --------------------------------- */

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Catalog"
        subtitle="Choose the source spreadsheet, which columns dealers see, and how the share message reads."
        actions={
          saved ? <Badge tone="success">Syncing: {saved.spreadsheetName ?? saved.spreadsheetId}</Badge> : undefined
        }
      />

      <Card>
        <div className="border-b border-slate-100 px-5 py-3 text-sm font-semibold text-slate-700">
          Spreadsheets shared with the app account
        </div>
        <div className="px-5 py-2 text-xs text-slate-400">
          To add one: share it (Viewer) with{' '}
          <code className="rounded bg-slate-100 px-1">{SHEETS_SA_EMAIL}</code>
        </div>
        {listError ? (
          <div className="px-5 pb-4"><ErrorState message={listError} /></div>
        ) : sheets === null ? (
          <div className="px-5 pb-4"><LoadingState label="Loading sheets…" /></div>
        ) : sheets.length === 0 ? (
          <div className="px-5 pb-4 text-sm text-slate-400">No spreadsheets shared with the app account yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sheets.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-800">{s.name}</p>
                  <p className="text-xs text-slate-400">
                    {saved?.spreadsheetId === s.id ? 'Currently syncing · ' : ''}
                    edited {new Date(s.modifiedTime).toLocaleString()}
                  </p>
                </div>
                <Button
                  variant={selectedId === s.id ? 'primary' : 'secondary'}
                  onClick={() => void inspect(s)}
                  disabled={inspecting}
                >
                  {selectedId === s.id ? 'Selected' : saved?.spreadsheetId === s.id ? 'Edit columns' : 'Select'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <CategoryOrderCard />

      {inspecting ? <LoadingState label="Reading sheet columns…" /> : null}

      {tabs ? (
        <>
          {Object.entries(tabs).map(([tab, cols]) => {
            const shareRows = share[tab] ?? [];
            const sample = samples[tab] ?? null;
            const attrsMap = new Map((sample?.attrs ?? []).map((a) => [a.k, a.v] as [string, string]));
            const kept = cols.filter((c) => c.keep);
            const pendingFmt: TabDisplayFormat = {
              labels: Object.fromEntries(
                cols.filter((c) => c.label.trim()).map((c) => [c.name, c.label.trim()]),
              ),
              tableBreaksBefore: cols.filter((c) => c.breakBefore).map((c) => c.name),
              share: shareRows.map<ShareLayoutItem>((row) =>
                row.kind === 'blank' ? { kind: 'blank' } : { kind: 'field', name: row.name, show: row.show },
              ),
            };
            const valueOf = (name: string) => attrsMap.get(name) ?? '—';
            const tableRows = specTableRows(
              kept
                .map((c) => [c.name, valueOf(c.name)] as [string, string])
                .filter(([, v]) => v !== sample?.modelNumber && v !== sample?.partNo),
              pendingFmt,
            );
            const messageText = [
              `*${sample?.modelNumber ?? 'MODEL NUMBER'}*`,
              ...buildSpecShareLines(
                kept
                  .map((c) => [c.name, valueOf(c.name)] as [string, string])
                  .filter(([, v]) => v !== sample?.modelNumber),
                pendingFmt,
              ),
            ].join('\n');

            return (
              <Card key={tab}>
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                  <span className="text-sm font-semibold text-slate-700">{tab}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">
                      {kept.length}/{cols.length} columns shown
                    </span>
                    <Button variant="secondary" onClick={() => resetToSheetOrder(tab)}>
                      Reset to sheet order
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col gap-6 px-5 py-4 xl:flex-row">
                  {/* ------------------------- table columns ------------------------- */}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Table columns</p>
                    <p className="mb-2 mt-0.5 text-[11px] text-slate-400">
                      Drag ⋮⋮ (or use ↑↓) to reorder — applies on the next sync. Renames and section
                      gaps are live the moment you save.
                    </p>
                    <ul className="divide-y divide-slate-50 rounded-lg border border-slate-100">
                      {cols.map((c, i) => (
                        <li key={c.name} {...dragProps(`cols:${tab}`, i, (f, t) => moveCol(tab, f, t))}>
                          {c.breakBefore && i > 0 ? <div className="h-1 bg-slate-200" /> : null}
                          <div className="flex items-center gap-2 px-3 py-2">
                            <span
                              {...handleProps(`cols:${tab}`, i)}
                              className="cursor-grab select-none px-1 text-slate-300 hover:text-slate-500"
                              title="Drag to reorder"
                            >
                              ⋮⋮
                            </span>
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-brand-600"
                              checked={c.keep}
                              onChange={() => toggleKeep(tab, i)}
                              aria-label={`Show ${c.name}`}
                            />
                            <span
                              className={`min-w-0 flex-1 truncate text-sm ${
                                c.keep ? 'text-slate-800' : 'text-slate-400 line-through'
                              }`}
                              title={c.name}
                            >
                              {c.name}
                              {c.priceLike ? (
                                <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600">
                                  PRICE — hidden by default
                                </span>
                              ) : null}
                            </span>
                            <input
                              type="text"
                              value={c.label}
                              onChange={(e) => renameCol(tab, i, e.target.value)}
                              placeholder="rename…"
                              className="w-36 rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 placeholder:text-slate-300 focus:border-brand-600 focus:outline-none"
                              aria-label={`Display label for ${c.name}`}
                            />
                            <button
                              type="button"
                              className={`rounded px-1.5 py-1 text-[10px] font-bold ${
                                c.breakBefore
                                  ? 'bg-slate-700 text-white'
                                  : 'text-slate-400 hover:bg-slate-100'
                              } disabled:opacity-30`}
                              onClick={() => toggleBreak(tab, i)}
                              disabled={i === 0}
                              title="Start a new section above this row"
                            >
                              GAP
                            </button>
                            <button
                              type="button"
                              className="rounded px-2 py-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                              onClick={() => moveCol(tab, i, i - 1)}
                              disabled={i === 0}
                              aria-label="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="rounded px-2 py-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                              onClick={() => moveCol(tab, i, i + 1)}
                              disabled={i === cols.length - 1}
                              aria-label="Move down"
                            >
                              ↓
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>

                    {/* ----------------------- share message ----------------------- */}
                    <p className="mt-5 text-xs font-bold uppercase tracking-wide text-slate-500">
                      Share / copy message
                    </p>
                    <p className="mb-2 mt-0.5 text-[11px] text-slate-400">
                      Its own order — reordering here does not touch the table. Unticked fields stay in
                      the table but leave the message. Live the moment you save.
                    </p>
                    <ul className="divide-y divide-slate-50 rounded-lg border border-slate-100">
                      {shareRows.map((row, i) => (
                        <li
                          key={row.id}
                          {...dragProps(`share:${tab}`, i, (f, t) => moveShare(tab, f, t))}
                          className="flex items-center gap-2 px-3 py-2"
                        >
                          <span
                            {...handleProps(`share:${tab}`, i)}
                            className="cursor-grab select-none px-1 text-slate-300 hover:text-slate-500"
                            title="Drag to reorder"
                          >
                            ⋮⋮
                          </span>
                          {row.kind === 'field' ? (
                            <>
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-brand-600"
                                checked={row.show}
                                onChange={() => toggleShow(tab, i)}
                                aria-label={`Include ${row.name} in the message`}
                              />
                              <span
                                className={`min-w-0 flex-1 truncate text-sm ${
                                  row.show ? 'text-slate-800' : 'text-slate-400 line-through'
                                }`}
                              >
                                {displayLabel(row.name, pendingFmt)}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="flex-1 text-xs italic tracking-widest text-slate-400">
                                — blank line —
                              </span>
                              <button
                                type="button"
                                className="rounded px-2 py-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                                onClick={() => removeBlank(tab, i)}
                                aria-label="Remove blank line"
                              >
                                ×
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            className="rounded px-2 py-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                            onClick={() => moveShare(tab, i, i - 1)}
                            disabled={i === 0}
                            aria-label="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="rounded px-2 py-1 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                            onClick={() => moveShare(tab, i, i + 1)}
                            disabled={i === shareRows.length - 1}
                            aria-label="Move down"
                          >
                            ↓
                          </button>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="mt-2 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:border-slate-400 hover:text-slate-700"
                      onClick={() => addBlank(tab)}
                    >
                      + Add blank line
                    </button>
                  </div>

                  {/* --------------------------- previews --------------------------- */}
                  <div className="flex flex-col gap-5 sm:flex-row xl:flex-col">
                    <PhonePreview
                      category={tab}
                      modelNumber={sample?.modelNumber ?? 'MODEL NUMBER'}
                      partNo={sample?.partNo}
                      rows={tableRows}
                      sampleMissing={sample === null}
                    />
                    <ShareMessagePreview text={messageText} />
                  </div>
                </div>
              </Card>
            );
          })}

          <div className="flex gap-3">
            <Button variant="primary" onClick={() => void save(true)} disabled={busy}>
              {busy ? 'Working…' : 'Save & sync now'}
            </Button>
            <Button variant="secondary" onClick={() => void save(false)} disabled={busy}>
              Save only
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
